class ColorUtils {
  static isValidHex(hex) {
    return /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(hex);
  }

  static expandHex(hex) {
    if (hex.length === 4) {
      const r = hex.charAt(1);
      const g = hex.charAt(2);
      const b = hex.charAt(3);
      return "#" + r + r + g + g + b + b;
    }
    return hex;
  }

  static hexToHsb(hex) {
    let r = parseInt(hex.slice(1, 3), 16) / 255,
        g = parseInt(hex.slice(3, 5), 16) / 255,
        b = parseInt(hex.slice(5, 7), 16) / 255,
        max = Math.max(r, g, b),
        min = Math.min(r, g, b),
        delta = max - min,
        h, s, v = max;

    s = max === 0 ? 0 : delta / max * 100;

    if (delta === 0) {
      h = 0;
    } else if (max === r) {
      h = 60 * (((g - b) / delta) % 6);
    } else if (max === g) {
      h = 60 * (((b - r) / delta) + 2);
    } else {
      h = 60 * (((r - g) / delta) + 4);
    }

    if (h < 0) h += 360;
    return { h, s, b: v * 100 };
  }

  static hsbToHex(h, s, b) {
    s /= 100;
    b /= 100;
    const k = n => (n + h / 60) % 6;
    const f = n => b - b * s * Math.max(Math.min(k(n), 4 - k(n), 1), 0);
    const rgb = [f(5), f(3), f(1)];
    return '#' + rgb.map(x => Math.round(x * 255).toString(16).padStart(2, '0')).join('');
  }

  static hexDifference(hex1, hex2) {
    hex1 = hex1.length === 4 ? this.expandHex(hex1) : hex1;
    hex2 = hex2.length === 4 ? this.expandHex(hex2) : hex2;

    const r1 = parseInt(hex1.substring(1, 3), 16),
          g1 = parseInt(hex1.substring(3, 5), 16),
          b1 = parseInt(hex1.substring(5, 7), 16);

    const r2 = parseInt(hex2.substring(1, 3), 16),
          g2 = parseInt(hex2.substring(3, 5), 16),
          b2 = parseInt(hex2.substring(5, 7), 16);

    return Math.max(
      Math.abs(r1 - r2),
      Math.abs(g1 - g2),
      Math.abs(b1 - b2)
    );
  }
}

class ColorPaletteItem {
  constructor({
    input,
    template,
    selectors,
    classNames,
    appendTo
  }) {
    this.input = input;
    this.template = template;
    this.selectors = selectors;
    this.classNames = classNames;

    this.root = input.closest(appendTo);
    if (!this.root) return;

    this.element = this.createElementFromTemplate();
    this.textInput = this.root.querySelector(this.selectors.textInput);
    this.area = this.element.querySelector(this.selectors.area);
    this.hueInput = this.element.querySelector(this.selectors.hue);
    this.brightnessInput = this.element.querySelector(this.selectors.brightness);
    this.saturationInput = this.element.querySelector(this.selectors.saturation);

    this.root.appendChild(this.element);
    this.init();
  }

  // ----- Initialization -----
  init() {
    this.input.setAttribute("tabindex", "-1");
    this.addEventListeners();
  }

  createElementFromTemplate() {
    const parent = document.createElement("div");
    parent.innerHTML = this.template;
    return parent.firstElementChild;
  }

  // ----- Display -----
  show() {
    this.setColor(this.input.value);
    this.toggle(true);
  }

  hide() {
    this.toggle(false);
  }

  isVisible() {
    return this.element.classList.contains(this.classNames.visible);
  }

  toggle(state) {
    this.element.classList.toggle(this.classNames.visible, state !== undefined ? state : !this.isVisible());
  }

  // ----- Value Handling -----
  setColor(hex) {
    const origHex = ColorUtils.expandHex(hex);
    const color = ColorUtils.hexToHsb(hex);
    this.hueInput.value = color.h;
    this.saturationInput.value = color.s;
    this.brightnessInput.value = color.b;
    this.updateUI(origHex);
  }

  updateUI(origHex) {
    const h = this.hueInput.value;
    const s = this.saturationInput.value;
    const b = this.brightnessInput.value;
    const hex = ColorUtils.hsbToHex(h, s, b);
    const hue = ColorUtils.hsbToHex(h, 100, 100);

    this.root.style.setProperty("--c", hex);
    this.element.style.setProperty("--c", hex);
    this.element.style.setProperty("--h", hue);
    this.element.style.setProperty("--x", `${s}%`);
    this.element.style.setProperty("--y", `${100 - b}%`);
    this.input.value = hex;
    this.input.dispatchEvent(new Event("input", { bubbles: true }));

    if (document.activeElement !== this.textInput) {
      this.textInput.value = origHex || hex;
    }
  }

  // ----- Input Events -----
  addEventListeners() {
    this.root.addEventListener("click", e => {
      if (this.element.contains(e.target)) e.preventDefault();
    });

    document.addEventListener("pointerdown", e => {
      if (!this.isVisible()) return;
      const label = e.target.closest(`label[for='${this.input.id}']`);
      if (!this.root.contains(e.target) && !label) this.hide();
    });

    document.addEventListener("click", e => {
      const label = e.target.closest(`label[for='${this.input.id}']`);
      if (!label || e.target.matches("input")) return;
      e.preventDefault();
      this.brightnessInput.focus();
    });

    this.element.addEventListener("pointerdown", this.onPointerDown.bind(this));
    this.input.addEventListener("change", e => this.setColor(e.target.value));

    this.root.addEventListener("focus", () => {
      if (!this.isVisible()) this.show();
    }, true);

    document.addEventListener("focus", e => {
      if (!this.root.contains(e.target) && this.isVisible()) this.hide();
    }, true);

    this.element.addEventListener("input", e => {
      if (e.target.dataset.elem) this.updateUI();
    });

    this.element.addEventListener("keydown", this.onKeyDown.bind(this));
    window.addEventListener("keydown", e => e.key === "Escape" && this.hide());

    this.textInput.addEventListener("input", this.onTextChange.bind(this));
    this.textInput.addEventListener("change", this.onTextChange.bind(this));
    this.textInput.addEventListener("beforeinput", this.onTextBeforeInput.bind(this));
    this.textInput.addEventListener("blur", this.onTextBlur.bind(this));
  }

  onKeyDown(e) {
    if (!e.target.matches("[type='range']") || !e.key.includes("Arrow")) return;
    e.preventDefault();

    let step = +(e.target.dataset.step || e.target.step || 1);
    if (e.shiftKey) step *= 10;
    if (["ArrowDown", "ArrowLeft"].includes(e.key)) step *= -1;

    if (e.target === this.saturationInput && ["ArrowUp", "ArrowDown"].includes(e.key)) {
      this.brightnessInput.value = +this.brightnessInput.value + step;
    } else {
      e.target.value = +e.target.value + step;
    }

    e.target.dispatchEvent(new Event("input", { bubbles: true }));
  }

  onPointerDown(e) {
    if (e.target === this.area || e.target.closest("[data-elem='thumb']")) {
      this.handleAreaPointer(e);
      return;
    }

    if (e.pointerType !== "mouse") return;

    const onHuePointerUp = () => {
      this.hueInput.classList.remove(this.classNames.hueInputActive);
      window.removeEventListener("pointerup", onHuePointerUp);
    };

    if (e.target === this.hueInput || e.target.closest("[data-elem='hue']")) {
      this.hueInput.classList.add(this.classNames.hueInputActive);
      window.addEventListener("pointerup", onHuePointerUp);
    }
  }

  handleAreaPointer(e) {
    let last = { x: e.clientX, y: e.clientY };
    const move = e => this.updateAreaValue(last = { x: e.clientX, y: e.clientY });
    const up = e => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      this.updateAreaValue({ x: e.clientX, y: e.clientY });
      this.saturationInput.classList.remove(this.classNames.areaInputActive);
    };

    this.saturationInput.classList.add(this.classNames.areaInputActive);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    this.updateAreaValue(last);
  }

  updateAreaValue(coords) {
    if (this._ticking) return;
    this._ticking = true;

    requestAnimationFrame(() => {
      const rect = this.area.getBoundingClientRect();
      const x = ((coords.x - rect.x) / rect.width) * 100;
      const y = 100 - ((coords.y - rect.y) / rect.height) * 100;
      this.setAreaValue({ x, y });
      this._ticking = false;
    });
  }

  setAreaValue({ x = 0, y = 0 }) {
    const sx = Math.max(0, Math.min(100, Math.round(x)));
    const sy = Math.max(0, Math.min(100, Math.round(y)));

    this.saturationInput.value = sx;
    this.brightnessInput.value = sy;

    this.saturationInput.dispatchEvent(new Event("input", { bubbles: true }));
    this.brightnessInput.dispatchEvent(new Event("input", { bubbles: true }));
  }

  onTextChange(e) {
    let color = e.target.value;
    if (!ColorUtils.isValidHex(color)) {
      if (ColorUtils.isValidHex("#" + color)) {
        color = "#" + color;
      } else {
        return;
      }
    }

    const expanded = ColorUtils.expandHex(color);
    if(expanded == this.input.value){
      return;
    }

    if (e.type === "change" && document.activeElement !== e.target) {
      e.target.value = expanded;
    }

    this.input.value = expanded;
    this.input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  onTextBeforeInput(e) {
    if (e.data && !/^[#A-Fa-f0-9]$/.test(e.data)) {
      e.preventDefault();
    }
  }

  onTextBlur(e) {
    const textInputColor = this.textInput.value;
    if(ColorUtils.isValidHex(this.textInput.value)){
      this.textInput.value = ColorUtils.expandHex(textInputColor);
      return;
    }
    this.textInput.value = this.input.value;
  }
}

class ColorPaletteController {
  constructor(settings) {
    this.settings = settings;
    this.palettes = [];
  }

  bindNew() {
    this.getUnboundInputs().forEach(input => {
      const palette = new ColorPaletteItem({ ...this.settings, input });
      this.palettes.push(palette);
    });
  }

  getUnboundInputs() {
    const allInputs = document.querySelectorAll(this.settings.selectors.input);
    const initialized = this.palettes.map(p => p.input);
    return Array.from(allInputs).filter(input => !initialized.includes(input));
  }
}

export {
  ColorPaletteController,
  ColorPaletteItem
};